import { createContext, useContext, useMemo, useState } from "react"
import { AboutData, aboutSchema, aboutSchema_en } from "../screens/AboutScreen"
import { FormikProps, useFormik } from "formik"
import { PositionData, positionSchema, positionSchema_en } from "../screens/PositionScreen";
import { LanguageData, languageSchema } from "../screens/LanguagesScreen";
import { CertificatesData, certificatesSchema } from "../screens/CertificatesScreen";
import { SkillsData, skillsSchema, skillsSchema_en } from "../screens/SkillsScreen";
import labels_de from '../labels/de'
import labels_en from '../labels/en'
import salesforceFieldmap from "../components/JobApplicationForm/salesforceFieldmap";

export enum Screens {
    ABOUT,
    POSITION,
    LANGUAEGS,
    CERTIFICATES,
    SKILLS,
}

const validationMap = {
    [Screens.ABOUT]: aboutSchema,
    [Screens.POSITION]: positionSchema,
    [Screens.LANGUAEGS]: certificatesSchema,
    [Screens.CERTIFICATES]: certificatesSchema,
    [Screens.SKILLS]: skillsSchema,
}
const validationMap_en = {
    [Screens.ABOUT]: aboutSchema_en,
    [Screens.POSITION]: positionSchema_en,
    [Screens.LANGUAEGS]: languageSchema,
    [Screens.CERTIFICATES]: certificatesSchema,
    [Screens.SKILLS]: skillsSchema_en,
}

const languageSkillMap = {
    0: '',
    1: 'Beginner',
    2: 'Intermediate',
    3: 'Advanced',
    4: 'Professional',
}

interface FormData_I extends AboutData, PositionData, LanguageData, CertificatesData, SkillsData {
    orgid: '00D2o000000a6mP',
    retURL: string,
    '00NOj0000026b5N': 'Crewing',
    cv: File | undefined,
 }

interface FormContextProps {
    lang?: 'de' | 'en',
        labels: typeof labels_de | typeof labels_en,
        formData: FormData_I,
    currentScreen: Screens,
    goToNextScreen: () => void,
    goToPreviousScreen: () => void,
    formik?: FormikProps<FormData_I>
}

const FormDefaults: FormContextProps = {
    lang: 'de',
    labels: labels_de,
    formData: {
        firstName: '',
        lastName: '',
        birthdate: '',
        pronouns: '',
        email: '',
        phone: '',
        nationality: '',
        residency: '',
        positions: [],// Fix: Update the type to allow for null values
        startDate: '', 
        moreInfoDate: '',
        cv: undefined,
        motivation: '',
        english: 0,
        italian: 0,
        arabic: 0,
        french: 0,
        additionalLanguages: '',
        basicSafety: false,
        medicalCertificate: false,
        rhibLicense: false,
        additionalCertificates: '',
        relevantSkills: [],
        otherRelevantSkills: '',
        medicalBackground: '',
        dataprotection: false,
        orgid: '00D2o000000a6mP',
        retURL: '',
        '00NOj0000026b5N': 'Crewing',
    },
    currentScreen: Screens.POSITION,// Screens.ABOUT,
    goToNextScreen: () => { },
    goToPreviousScreen: () => { },
    formik: undefined
}

const FormContext = createContext<FormContextProps>(FormDefaults)

interface FormProviderProps extends React.PropsWithChildren<{}> {
}

// function that converts yyyy-mm-dd to dd.mm.yyyy
function convertDate(date: string): string {
    const [year, month, day] = date.split('-')
    return `${day}.${month}.${year}`
}

export const FormContextProvider = (props: FormProviderProps) => {

    const lang = useMemo(() => {
        
        const htmlLang = document.querySelector('html')?.getAttribute('lang')
        if (htmlLang?.match(/^en(.*)?$/i)) {
            return 'en'
        }
        else return 'de'
    }, [])

    const labels = useMemo(() => {
        if (lang === 'de') return labels_de
        return labels_en
    }, [lang])

    const [currentScreen, _setCurrentScreen] = useState<Screens>(Screens.ABOUT)

    const onSubmit = async (values: FormData_I) => {

        if(!values.dataprotection) {
            return
        }

        const redirectUrl = document.querySelectorAll('.react-apply-button')[0]?.getAttribute('data-redirect-url') || 'https://sos-humanity.org' as string

        const preparedValues = {
            ...values,
            english: languageSkillMap[values.english as keyof typeof languageSkillMap],
            italian: languageSkillMap[values.italian as keyof typeof languageSkillMap],
            arabic: languageSkillMap[values.arabic as keyof typeof languageSkillMap],
            french: languageSkillMap[values.french as keyof typeof languageSkillMap],
            startDate: convertDate(values.startDate as string),
            birthdate: convertDate(values.birthdate as string),
            positions: values.positions ? values.positions.join(';') : '',
            relevantSkills: values.relevantSkills ? values.relevantSkills.join(';') : '',
            dataprotection: '1',
            retURL: redirectUrl,
        }

        const form = document.createElement('form');
        form.setAttribute('method', 'POST');
        form.setAttribute('action', 'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00D2o000000a6mP');
        form.style.display = 'none'; 

        // Transform and append the values to the form
        for (const key in preparedValues) {
            const mappedKey = salesforceFieldmap[key as keyof typeof salesforceFieldmap];
            if (mappedKey) {
                const input = document.createElement('input');
                input.setAttribute('type', 'hidden');
                input.setAttribute('name', mappedKey);

                const v = preparedValues[key as keyof FormData_I];
                
                if (typeof v === 'boolean') {
                    input.setAttribute('value', v ? '1' : '0');
                } else {
                    input.setAttribute('value', String(preparedValues[key as keyof FormData_I]));
                }

                form.appendChild(input);
            }
        }

        // add debug info
        // const debugFields = {
        //     debug: '1',
        //     debugEmail: ''
        // }   

        // for (const key in debugFields) {
        //     const input = document.createElement('input');
        //     input.setAttribute('type', 'hidden');
        //     input.setAttribute('name', key);
        //     input.setAttribute('value', String(debugFields[key as keyof typeof debugFields]));
        //     form.appendChild(input);
        // }

        // // Append the form to the body
        document.body.appendChild(form);
        
        // upload the cv to the wordpress api

        const wpBaseUrl = 'https://sos-humanity.org'
        const wpApiUrl = `${wpBaseUrl}/wp-json/flynt/v1/submit_form`
        const wpFormData = new FormData(form)

        //http://soshumanity.local/wp-json/flynt/v1/submit_form

        // appen wpForm with input for values.cv where values.cv is a File
        
        if (values.cv) {
            wpFormData.append('cv', values.cv)
        }

        // send the form data to the wordpress api
        fetch(wpApiUrl, {
            method: 'POST',
            body: wpFormData,
            headers: {
                'Accept': 'application/json',
            },
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            return response.json();
        })
        .then(data => {
            form.submit();
        })
        .catch(error => {
            console.error('Error:', error);
        });
    }


    const formik = useFormik({
        initialValues: sessionStorage.getItem('sos-humanity-job-application-formData') ?
            (() => { const d = JSON.parse(sessionStorage.getItem('sos-humanity-job-application-formData') as string); return { ...d, cv: undefined } })()
            : FormDefaults.formData,
        validationSchema: lang === 'de' ? validationMap[currentScreen] : validationMap_en[currentScreen],
        onSubmit: onSubmit
    })

    const goToNextScreen = () => {

        formik.validateForm().then((errors) => {
            formik.setErrors(errors)

            formik.setTouched(Object.keys(formik.values).reduce((acc, key) => {
                acc[key as keyof typeof acc] = true
                return acc
            }
                , {} as Record<string, boolean>))

            if (Object.keys(errors).length === 0) {
                if (currentScreen !== Screens.SKILLS) {
                    _setCurrentScreen(currentScreen + 1)
                }

                // save formData to session storage
                sessionStorage.setItem('sos-humanity-job-application-formData', JSON.stringify({ ...formik.values, dataprotection: false }))
            }
        })
    }

    const goToPreviousScreen = () => {
        if (currentScreen !== Screens.ABOUT) {
            _setCurrentScreen(currentScreen - 1)
        }
    }

    const values: FormContextProps = {
        lang,
        labels,
        formData: formik.values,
        currentScreen,
        goToNextScreen,
        goToPreviousScreen,
        formik,
    }

    return (
        <FormContext.Provider value={values}>
            {props.children}
        </FormContext.Provider>
    )
}

export const useFormContext = () => {
    const context = useContext(FormContext)
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider')
    }
    return context
}

export default FormContext
